
export type AccountType = 'client' | 'contractor' | 'friend' | 'applicant' | 'employee' | 'admin';

export interface User {
  id: string;
  name: string;
  role: 'client' | 'staff' | 'guest' | 'applicant';
  staffRole?: 'admin' | 'employee' | 'contractor';
  accountType?: AccountType;
  email: string;
  address?: string;
}

export enum JobStatus {
  LEAD = 'LEAD',
  ESTIMATING = 'ESTIMATING',
  PENDING = 'PENDING',
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  INVOICED = 'INVOICED',
  PAID = 'PAID'
}

export enum TreeHealth {
  EXCELLENT = 'EXCELLENT',
  GOOD = 'GOOD',
  FAIR = 'FAIR',
  POOR = 'POOR',
  CRITICAL = 'CRITICAL'
}

export interface Activity {
  id: string;
  type: 'LEAD' | 'JOB' | 'DRONE' | 'FINANCE' | 'SYSTEM';
  message: string;
  timestamp: string;
  user: string;
}

export interface Tree {
  id: string;
  species: string;
  diameter: number;
  /** Field-estimated total height in feet (used by the 3D survey). */
  height?: number;
  /** Canopy radius in feet (used by the 3D survey). */
  canopyRadius?: number;
  health: TreeHealth;
  location: string;
  notes: string;
  photoUrl?: string;
  x?: number; // Relative X coordinate on property plot (0-100)
  y?: number; // Relative Y coordinate on property plot (0-100)
}

export interface Drone {
  id: string;
  model: string;
  serialNumber: string;
  lastMaintenance: string;
  status: 'Available' | 'In Flight' | 'Maintenance';
  assignedPilot: string;
}

export interface DroneMission {
  id: string;
  jobId: string;
  droneId: string;
  status: 'Pending Approval' | 'Planned' | 'In Progress' | 'Completed' | 'Denied';
  missionType: 'Site Survey' | 'Health Inspection' | 'Storm Assessment' | 'Post-Work Verification';
  aerialMedia: string[]; // URLs or base64
  aiSummary?: string;
  price?: number;
  customerNotes?: string;
  flightAltitude?: number;
  resolution?: 'Standard' | 'High' | '4K';
  deliverables?: string[];
  adminNotes?: string;
  pilotInstructions?: string;
  scheduledDate?: string;
  reportGenerated?: boolean;
  conferenceScheduled?: boolean;
  invoiced?: boolean;
  paid?: boolean;
}

export interface MissionTemplate {
  id: string;
  name: 'Site Survey' | 'Health Inspection' | 'Storm Assessment' | 'Post-Work Verification';
  description: string;
  defaultAltitude: number;
  requiredPayload: string;
  estimatedDurationMins: number;
  basePrice: number;
  deliverables: string[];
}

export interface FlightLog {
  id: string;
  missionId: string;
  droneId: string;
  pilotId: string;
  date: string;
  durationMins: number;
  maxAltitude: number;
  batteryUsagePercent: number;
  weatherConditions: string;
  pilotNotes: string;
  telemetryDataUrl?: string;
}

export interface MissionReport {
  id: string;
  missionId: string;
  jobId: string;
  dateGenerated: string;
  aiAnalysis: string;
  mediaLinks: string[];
  identifiedHazards: string[];
  recommendations: string[];
  status: 'Draft' | 'Published';
}

export interface LocalEvent {
  id: string;
  title: string;
  organizer: string;
  date: string;
  type: 'Class' | 'Planting' | 'Weather' | 'Meeting' | 'Cleanup' | 'Festival' | 'Collection';
  description: string;
  link: string;
}

export interface ResourceLink {
  id: string;
  title: string;
  description: string;
  url: string;
  category: 'Government' | 'Education' | 'Non-Profit' | 'Emergency';
}

export interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
}

export interface Lead {
  id: string;
  clientName: string;
  email: string;
  phone: string;
  address: string;
  serviceType: 'Removal' | 'Pruning' | 'Plant Health' | 'Emergency' | 'Consulting';
  status: JobStatus;
  createdAt: string;
  attachments?: string[];
}

export interface WorkerCommitment {
  workerId: string;
  workerName: string;
  role: string;
  equipment: string[];
}

export interface JSHA {
  id: string;
  date: string;
  assessedBy: string;
  hazards: {
    overhead: boolean;
    underground: boolean;
    traffic: boolean;
    weather: boolean;
    wildlife: boolean;
    publicAccess: boolean;
    steepTerrain: boolean;
    other: string;
  };
  ppe: {
    helmet: boolean;
    eyeProtection: boolean;
    earProtection: boolean;
    chainsawChaps: boolean;
    highViz: boolean;
    gloves: boolean;
    climbingGear: boolean;
  };
  emergencyPlan: {
    nearestHospital: string;
    musterPoint: string;
    firstAidKitLocation: string;
  };
  completed: boolean;
}

export enum JobType {
  TREE_WORK = 'TREE_WORK',
  DRONE_MISSION = 'DRONE_MISSION'
}

export interface Job extends Lead {
  type?: JobType;
  scheduledDate?: string;
  crewId?: string;
  dispatchStatus?: 'PENDING' | 'EN_ROUTE' | 'ON_SITE' | 'COMPLETED';
  commitments?: WorkerCommitment[];
  inventory: Tree[];
  lineItems: LineItem[];
  totalPrice: number;
  safetyChecked: boolean;
  jsha?: JSHA;
  documents: PropertyDocument[];
  missionDetails?: Partial<DroneMission>;
}

export interface Expense {
  id: string;
  merchant: string;
  amount: number;
  date: string;
  category: string;
  notes?: string;
  receiptUrl?: string;
}

export interface PropertyDocument {
  id: string;
  title: string;
  type: 'Manual' | 'Warranty' | 'Permit' | 'Other';
  summary: string;
  dateAdded: string;
  url?: string;
}

export interface Crew {
  id: string;
  name: string;
  members: string[];
}

export interface Contact {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  companyName?: string;
  notes?: string;
  /** Classifies the person so staff, contractors and imports stay out of the customer list. */
  personType?: 'CUSTOMER' | 'EMPLOYEE' | 'CONTRACTOR' | 'OTHER';
  createdAt: string;
  userId?: string;
}

export interface FinancialRecord {
  id: string;
  type: 'ESTIMATE' | 'INVOICE';
  status: 'DRAFT' | 'SENT' | 'PAID' | 'OVERDUE';
  contactId: string;
  jobId: string;
  amount: number;
  date: string;
  dueDate?: string;
  lineItems: LineItem[];
  createdAt: string;
}

export interface TimeEntry {
  id: string;
  userId: string;
  userName: string;
  date: string;
  hours: number;
  description: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'PAID';
}

export interface ContractorInvoice {
  id: string;
  contractorId: string;
  contractorName: string;
  amount: number;
  date: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'PAID';
  description: string;
  invoiceUrl?: string;
}

/** Company stock item — equipment, materials and supplies tracked for jobs.
 *  (Distinct from Job.inventory, which lists trees on a customer property.) */
export interface InventoryItem {
  id: string;
  name: string;
  sku: string;
  category: 'Equipment' | 'Fuel' | 'Climbing Gear' | 'Chainsaw Parts' | 'PPE' | 'Chemicals' | 'Office' | 'Other';
  quantity: number;
  unit: string;              // e.g. gal, each, box, lb
  unitCost: number;
  supplier: string;
  location: string;          // e.g. Shop A, Truck 2
  reorderPoint: number;
  notes?: string;
  status: 'IN_STOCK' | 'LOW' | 'OUT_OF_STOCK' | 'RETIRED';
  isDeleted?: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface LedgerEntry {
  id: string;
  transactionId: string;
  date: string;
  type: 'Income' | 'Expense';
  description: string;
  payerPayee: string;
  category: string;
  amount: number;
  paymentMethod: string;
  reconciled: boolean;
  referenceUrl?: string;
}

export interface TreeSurvey {
  id: string;
  jobId?: string;
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  surveyorName: string;
  purpose: string;
  siteAddress: string;
  surveyArea: string;
  date: string;
  aiSiteData?: {
    typicalSpecies: string[];
    soilConditions: string;
    weatherPatterns: string;
  };
  trees: Tree[];
  createdAt: string;
}

export interface JobBriefing {
  id: string;
  jobId: string;
  clientName: string;
  address: string;
  scopeOfWork: string;
  crewMembers: string[];
  date: string;
  signedOffBy: string;
  createdAt: string;
}

export interface RiskAssessment {
  id: string;
  jobId: string;
  date: string;
  assessorName: string;
  weatherConditions: string;
  groundConditions: string;
  ppeRequirements: string[];
  identifiedRisks: string[];
  mitigationSteps: string[];
  createdAt: string;
}

export interface Client {
  name: string;
  email: string;
  phone: string;
  address: string;
  jobs: Job[];
  leads: Lead[];
}

export interface NotificationItem {
  id: string;
  title: string;
  message: string;
  timestamp: string;
  type: NotificationType;
  read: boolean;
  meta?: any;
}

/**
 * Notification channels used by the dashboard alert console.
 * APPLICATION / MISSION are the original Arb-E channels; the rest were added
 * for the owner's day planner and site-activity watcher.
 */
export type NotificationType =
  | 'APPLICATION'
  | 'MISSION'
  | 'SYSTEM'
  | 'APPOINTMENT'
  | 'ESTIMATE'
  | 'ACCOUNT'
  | 'ACTIVITY';

/** A single item in the dashboard's site-activity box. */
export type SiteActivityKind = 'ACCOUNT' | 'ESTIMATE_REQUEST' | 'BOOKING' | 'WORKSHEET';

export interface SiteActivityItem {
  /** Stable `${kind}:${documentId}` key so re-reads never duplicate a row. */
  id: string;
  kind: SiteActivityKind;
  title: string;
  detail: string;
  actorName?: string;
  actorEmail?: string;
  createdAt: string;
  /** In-app tab that gives the owner the full record. */
  route?: string;
}
